import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronDown, Sparkles } from 'lucide-react';
import Button from './ui/Button';
import styled from 'styled-components';
import { float, floatDelayed, fadeIn, pulse } from '../styles/animations';

const HeroContainer = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  id: hero;
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    hsl(200, 85%, 55%) 0%,
    hsl(200, 75%, 65%) 25%,
    hsl(210, 70%, 60%) 50%,
    hsl(200, 80%, 58%) 75%,
    hsl(195, 90%, 55%) 100%
  );
`;

const FloatingElements = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const FloatingElement = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  animation: ${float} 8s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 5rem;
    left: 2.5rem;
    width: 16rem;
    height: 16rem;
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:nth-child(2) {
    bottom: 5rem;
    right: 2.5rem;
    width: 24rem;
    height: 24rem;
    background: ${props => props.theme.colors.accent.main}33;
    animation: ${floatDelayed} 10s ease-in-out infinite;
    animation-delay: 2s;
  }
  
  &:nth-child(3) {
    top: 33%;
    right: 25%;
    width: 8rem;
    height: 8rem;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const SparkleContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const SparkleIcon = styled(Sparkles)`
  position: absolute;
  color: rgba(255, 255, 255, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 25%;
    left: 25%;
    width: 1.5rem;
    height: 1.5rem;
  }
  
  &:nth-child(2) {
    top: 33%;
    right: 33%;
    width: 1rem;
    height: 1rem;
    animation-delay: 1s;
  }
  
  &:nth-child(3) {
    bottom: 33%;
    left: 33%;
    width: 1.25rem;
    height: 1.25rem;
    animation-delay: 2s;
  }
  
  &:nth-child(4) {
    bottom: 25%;
    right: 25%;
    width: 1.5rem;
    height: 1.5rem;
    animation-delay: 0.5s;
  }
`;

const Content = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
  position: relative;
  z-index: 10;
  text-align: center;
`;

const ContentWrapper = styled.div`
  max-width: 64rem;
  margin: 0 auto;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.light};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: ${props => props.theme.spacing['2xl']};
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['5xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary.foreground};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  line-height: 1.1;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['9xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['10xl']};
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${props => props.theme.typography.fontSize.lg};
  max-width: 42rem;
  margin: 0 auto ${props => props.theme.spacing['3xl']};
  line-height: 1.7;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
    max-width: 48rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    max-width: 56rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    max-width: 64rem;
  }
`;

const CTAWrapper = styled.div`
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.4s;
  opacity: 0;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s infinite;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }
`;

const ScrollLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    color: rgba(255, 255, 255, 1);
  }
  
  span {
    font-size: ${props => props.theme.typography.fontSize.sm};
    margin-bottom: ${props => props.theme.spacing.sm};
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToFeatured = () => {
    const element = document.getElementById('featured');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer id="hero">
      <HeroGradient />
      
      <FloatingElements>
        <FloatingElement />
        <FloatingElement />
        <FloatingElement />
      </FloatingElements>

      <SparkleContainer>
        <SparkleIcon />
        <SparkleIcon />
        <SparkleIcon />
        <SparkleIcon />
      </SparkleContainer>

      <Content>
        <ContentWrapper>
          <Subtitle>{t('hero.subtitle')}</Subtitle>
          
          <Title>{t('hero.title')}</Title>
          
          <Description>{t('hero.description')}</Description>
          
          <CTAWrapper>
            <Button
              variant="hero"
              size="lg"
              onClick={scrollToFeatured}
            >
              <Sparkles style={{ width: '1.25rem', height: '1.25rem' }} />
              {t('hero.cta')}
            </Button>
          </CTAWrapper>
        </ContentWrapper>

        <ScrollIndicator>
          <ScrollLink href="#why-blue">
            <span>{t('hero.scroll')}</span>
            <ChevronDown />
          </ScrollLink>
        </ScrollIndicator>
      </Content>
    </HeroContainer>
  );
};

export default HeroSection;
